'use client';

import { useAppDispatch, useAppSelector } from '@/lib/redux/hooks';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import LoginButton from '@/components/auth/LoginButton';
import { useAuth } from '@/hooks/useAuth';
import { useAuthUser } from '@/hooks/useAuthUser';
import { usePathname, useRouter } from '@/i18n/routing';
import {
  selectAllHistorySessions,
  selectIsHistoryLoading,
} from '@/lib/redux/selectors/historySelectors';
import {
  type SessionListType,
  fetchSessionsHistory as fetchHistory,
  fetchMoreSessionsHistory,
  selectDirectSharedSessions,
  selectGroupSharedSessions,
  selectHistoryPagination,
} from '@/lib/redux/slices/history';
import {
  fetchUserUsage,
  selectIsUsageLoading,
  selectUsageData,
  selectUsageError,
} from '@/lib/redux/slices/usageSlice';
import { MOBILE_VIEW_BREAKPOINT_PX } from '@/utils/breakpoints';
import { differenceInDays, isToday, isYesterday } from 'date-fns';
import {
  Building2,
  ChevronDown,
  Loader2,
  RefreshCw,
  Users,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';

// SearchHistoryDialog must be dynamic here too — the MainContentArea also
// imports it via next/dynamic, but if any OTHER importer (this one) loads
// it eagerly, webpack puts it in the synchronous bundle anyway, bypassing
// the deferral. Both call sites must use dynamic for the chunk to truly
// defer. Same applies to its 5 framer-motion-using history child components.
const SearchHistoryDialog = dynamic(
  () =>
    import('./SearchHistoryDialog').then((m) => ({
      default: m.SearchHistoryDialog,
    })),
  { ssr: false }
);
import { AppSidebarSkeleton } from './AppSidebarSkeleton';
import { SessionItem, type SessionPathname } from './SessionItem';
import { SessionList } from './SessionList';
import { SidebarAnonFooter } from './SidebarAnonFooter';
import { SidebarRail } from './SidebarRail';
import { SidebarUsageInfo } from './SidebarUsageInfo';
import { getActiveSection } from './sidebarSections';

// Maps a sidebar section to the session class its context panel lists. Sections
// absent from this map have no session list (they render an info/inbox panel).
const SECTION_SESSION_LIST_TYPE: Partial<Record<string, SessionListType>> = {
  chat: 'chat',
  searchLegislation: 'search',
  analyzeDocument: 'document_analysis',
  generateDocument: 'document_generation',
};

// Interface for Session object
interface Session {
  session_id: string;
  sess_id: number;
  title: string;
  timestamp: number;
  isActive?: boolean;
  messagesFetched: boolean;
  access_role?: 'owner' | 'shared_direct' | 'shared_group';
  share_count?: number;
  owner_name?: string | null;
  org_id?: string | null;
}

export const AppSidebar = ({
  onRequestCollapse,
  isAnonymousMode = false,
}: {
  // Collapses the whole sidebar to the 56px icon rail (desktop) or closes the
  // drawer (mobile).
  onRequestCollapse?: () => void;
  // When true, shows a simplified sidebar for anonymous users
  // with only Search Legislation and Info menu (no auth-required features)
  isAnonymousMode?: boolean;
}) => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isDirectSharedOpen, setIsDirectSharedOpen] = useState(false);
  const [isGroupSharedOpen, setIsGroupSharedOpen] = useState(false);
  // Per-instance ids for the disclosure controlled-list wiring (aria-controls →
  // <ul id>). AppSidebar mounts twice concurrently on mobile (off-canvas drawer
  // + CSS-hidden desktop grid cell), so a hardcoded id would collide → invalid
  // duplicate ids and ambiguous aria-controls resolution. useId() is stable
  // across SSR/CSR and unique per mount (WAI-ARIA APG disclosure pattern).
  const directSharedListId = useId();
  const groupSharedListId = useId();
  // How the Recents list is grouped: 'date' (Today / Yesterday / … headers) or
  // 'none' (flat chronological). claude.com's "Group by" control. Persisted.
  const [recentsGroupBy, setRecentsGroupBy] = useState<'date' | 'none'>('date');
  const dragStartXRef = useRef<number>(0);

  const { isInOrg } = useAuthUser();
  const sessions = useAppSelector(selectAllHistorySessions);
  const directSharedSessions = useAppSelector(selectDirectSharedSessions);
  const groupSharedSessions = useAppSelector(selectGroupSharedSessions);
  const isLoading = useAppSelector(selectIsHistoryLoading);
  const dispatch = useAppDispatch();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Redux usage state (data is fetched globally by UsageInitializer)
  const usageData = useAppSelector(selectUsageData);
  const isUsageLoading = useAppSelector(selectIsUsageLoading);
  const usageError = useAppSelector(selectUsageError);

  const router = useRouter();
  const pathname = usePathname();
  const activeSection = getActiveSection(pathname);
  // The recents list always needs SOMETHING to show. On routes that belong to
  // no section (e.g. /user-profile) getActiveSection returns null — the recents
  // list falls back to the chat history so it stays reachable everywhere.
  const panelSection = activeSection ?? 'chat';
  // Which session class the recents list shows for this section (chat | search
  // | document_analysis), or undefined for sections with no session list (then
  // we fall back to the chat history).
  const sessionListType = SECTION_SESSION_LIST_TYPE[panelSection];
  // Pagination state for the active section's session list
  const pagination = useAppSelector(
    selectHistoryPagination(sessionListType ?? 'chat')
  );
  // Where a session in this section opens. Search sessions open the search
  // session view, document-analysis sessions their own view; chat (and any
  // unmapped section) fall back to the chat view, which renders any type.
  const sectionSessionPathname: SessionPathname =
    sessionListType === 'search'
      ? '/search/[sessionId]'
      : sessionListType === 'document_analysis'
        ? '/document-analysis/[sessionId]'
        : sessionListType === 'document_generation'
          ? '/generovanie/[sessionId]'
          : '/chat/[sessionId]';
  const { isAuthenticated, isLoading: isAuthLoading, unifiedUser } = useAuth();

  // Check if user has a paid plan via zadanie data (replaces old Stripe check).
  // Gates on the typed `is_paid_plan` flag, never the display name — see
  // ZadaniePlanInfo.is_paid_plan.
  const zadanie = usageData?.zadanie ?? null;
  const isPremiumActive = zadanie?.plan.is_paid_plan === true;

  const t = useTranslations('AppSidebar');
  const tHeader = useTranslations('Header');
  const tLoader = useTranslations('loading');

  // Refresh usage data (for retry on error or manual refresh)
  const fetchUsageData = useCallback(() => {
    if (unifiedUser?.id && !isUsageLoading) {
      dispatch(fetchUserUsage());
    }
  }, [unifiedUser?.id, isUsageLoading, dispatch]);

  const loadMoreSessions = useCallback(() => {
    if (
      !pagination.hasMore ||
      pagination.isLoadingMore ||
      !pagination.nextCursor
    )
      return;
    dispatch(
      fetchMoreSessionsHistory({
        type: sessionListType ?? 'chat',
        cursor: pagination.nextCursor,
      })
    );
  }, [dispatch, sessionListType, pagination]);

  const handleSessionListScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const nearBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - 120;
      if (
        nearBottom &&
        !pagination.isLoadingMore &&
        !pagination.loadMoreError
      ) {
        loadMoreSessions();
      }
    },
    [loadMoreSessions, pagination.isLoadingMore, pagination.loadMoreError]
  );

  // Grouping logic
  const getSessionGroup = (timestamp: number): string => {
    const date = new Date(timestamp);
    const now = new Date();
    if (isToday(date)) return 'today';
    if (isYesterday(date)) return 'yesterday';
    const daysDiff = differenceInDays(now, date);
    if (daysDiff <= 7) return 'previous7Days';
    if (daysDiff <= 30) return 'previous30Days';
    return 'older';
  };

  const sortedSessions = [...sessions]
    .filter((s) => s.access_role === 'owner')
    .sort((a, b) => b.timestamp - a.timestamp);

  // Show context badges only when there's a mix of personal and org sessions
  const showContextBadge =
    isInOrg &&
    sortedSessions.some((s) => s.org_id) &&
    sortedSessions.some((s) => !s.org_id);

  const groupedSessions = sortedSessions.reduce<Record<string, Session[]>>(
    (acc, session) => {
      const groupKey = getSessionGroup(session.timestamp);
      const translatedGroup = t(groupKey as Parameters<typeof t>[0]); // dynamic key
      if (!acc[translatedGroup]) {
        acc[translatedGroup] = [];
      }
      acc[translatedGroup].push(session);
      return acc;
    },
    {}
  );

  // Define the order of groups
  const groupOrder = [
    t('today'),
    t('yesterday'),
    t('previous7Days'),
    t('previous30Days'),
    t('older'),
  ];

  // Fetch the recents list for the active section (chat / search /
  // document_analysis). Sections with no session class of their own fall back
  // to the chat history, so recents is populated on every route.
  useEffect(() => {
    if (!isAuthLoading && isAuthenticated) {
      dispatch(fetchHistory(sessionListType ?? 'chat'));
    }
  }, [isAuthLoading, isAuthenticated, dispatch, sessionListType]);

  // Load the persisted Recents group-by preference once on mount.
  useEffect(() => {
    try {
      const persisted = localStorage.getItem('researchRecentsGroupBy');
      if (persisted === 'none' || persisted === 'date') {
        setRecentsGroupBy(persisted);
      }
    } catch {
      // noop – localStorage might be blocked
    }
  }, []);

  // Persist the Recents group-by preference whenever it changes.
  useEffect(() => {
    try {
      localStorage.setItem('researchRecentsGroupBy', recentsGroupBy);
    } catch {
      // noop
    }
  }, [recentsGroupBy]);

  // Simplified useEffect for initial state and mobile view
  useEffect(() => {
    // Only handle mobile view detection
    const handleResize = () => {
      // Same threshold as the `mobile-view:` CSS variant and MainContentArea's
      // drawer gate — a divergent value here puts the rail in desktop
      // hover-expand mode while CSS renders the mobile drawer (#5452).
      const isMobile = window.innerWidth < MOBILE_VIEW_BREAKPOINT_PX;
      setIsMobileView(isMobile);
      // Removed logic to set isOpen based on localStorage or mobile view
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Dependency array is empty as it only runs once now

  // Handle start new chat button click
  const handleStartNewChat = useCallback(() => {
    // Close mobile drawer before navigating
    if (isMobileView) {
      onRequestCollapse?.();
    }
    fetchUsageData();
    router.push('/chat');
  }, [fetchUsageData, router, isMobileView, onRequestCollapse]);

  // Listen for deferred actions when the sidebar reopens from collapsed rail
  useEffect(() => {
    const handleNewChat = () => {
      // eslint-disable-next-line no-console
      console.debug('[AppSidebar] received newChat');
      handleStartNewChat();
    };
    const handleSearch = () => {
      // eslint-disable-next-line no-console
      console.debug('[AppSidebar] received search');
      // Keep sidebar open and show modal on top
      setIsSearchOpen(true);
    };
    window.addEventListener('sidebar:newChat', handleNewChat);
    window.addEventListener('sidebar:search', handleSearch);
    return () => {
      window.removeEventListener('sidebar:newChat', handleNewChat);
      window.removeEventListener('sidebar:search', handleSearch);
    };
  }, [handleStartNewChat]);

  // Drag handlers for collapse/expand
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartXRef.current = e.clientX;
  }, []);

  const handleDragMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaX = e.clientX - dragStartXRef.current;
      const COLLAPSE_THRESHOLD = -50; // Drag left 50px to collapse

      if (deltaX < COLLAPSE_THRESHOLD) {
        // Dragged left enough, collapse the sidebar
        setIsDragging(false);
        onRequestCollapse?.();
      }
    },
    [isDragging, onRequestCollapse]
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Attach/detach mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleDragMove);
      document.addEventListener('mouseup', handleDragEnd);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', handleDragMove);
        document.removeEventListener('mouseup', handleDragEnd);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
    return undefined;
  }, [isDragging, handleDragMove, handleDragEnd]);

  const onSessionClick = isMobileView ? () => onRequestCollapse?.() : undefined;

  // The live chat-history list shown in the context panel for the chat
  // section. Owns the auth / loading / empty states.
  const chatHistory =
    isAuthLoading || isLoading ? (
      <AppSidebarSkeleton />
    ) : !isAuthenticated ? (
      <div className='flex justify-center items-center h-full text-center text-content-muted'>
        <div className='flex flex-col items-center'>
          <LoginButton t={tHeader as (key: string) => string} />
        </div>
      </div>
    ) : sortedSessions.length > 0 ||
      (isInOrg &&
        (directSharedSessions.length > 0 || groupSharedSessions.length > 0)) ? (
      <>
        {/* Shared folders — shown above own sessions, org members only */}
        {isInOrg && directSharedSessions.length > 0 && (
          <div className='ml-2 mt-1'>
            <button
              type='button'
              onClick={() => setIsDirectSharedOpen((v) => !v)}
              className='sticky top-0 bg-surface-sidebar w-full flex items-center gap-1 text-xs font-medium text-content-muted mt-1 mb-0 px-1.5 py-0.5 min-h-[24px] z-10 rounded-sm hover:text-brand-tertiary transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-surface-sidebar'
              aria-expanded={isDirectSharedOpen}
              aria-controls={directSharedListId}
            >
              <Users className='h-2.5 w-2.5 shrink-0' aria-hidden='true' />
              <span className='flex-1 text-left'>{t('sharedWithMe')}</span>
              <span className='text-content-muted tabular-nums'>
                {directSharedSessions.length}
              </span>
              <ChevronDown
                className={`h-3 w-3 shrink-0 transition-transform ${isDirectSharedOpen ? 'rotate-180' : ''}`}
                aria-hidden='true'
              />
            </button>
            {/* Kept mounted (hidden when collapsed) so `aria-controls` is never
                a dangling IDREF — the WAI-ARIA APG disclosure pattern requires the
                controlled element to exist. `hidden` removes it from the a11y tree
                and tab order when collapsed (#6453). */}
            <ul
              id={directSharedListId}
              hidden={!isDirectSharedOpen}
              className='space-y-0.5 pt-1 pb-1'
            >
              {directSharedSessions.map((session) => (
                <SessionItem
                  key={session.session_id}
                  session={session}
                  showContextBadge={showContextBadge}
                  onSessionClick={onSessionClick}
                />
              ))}
            </ul>
          </div>
        )}
        {isInOrg && groupSharedSessions.length > 0 && (
          <div className='ml-2 mt-1'>
            <button
              type='button'
              onClick={() => setIsGroupSharedOpen((v) => !v)}
              className='sticky top-0 bg-surface-sidebar w-full flex items-center gap-1 text-xs font-medium text-content-muted mt-1 mb-0 px-1.5 py-0.5 min-h-[24px] z-10 rounded-sm hover:text-brand-tertiary transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-surface-sidebar'
              aria-expanded={isGroupSharedOpen}
              aria-controls={groupSharedListId}
            >
              <Building2 className='h-2.5 w-2.5 shrink-0' aria-hidden='true' />
              <span className='flex-1 text-left'>{t('orgShared')}</span>
              <span className='text-content-muted tabular-nums'>
                {groupSharedSessions.length}
              </span>
              <ChevronDown
                className={`h-3 w-3 shrink-0 transition-transform ${isGroupSharedOpen ? 'rotate-180' : ''}`}
                aria-hidden='true'
              />
            </button>
            {/* Kept mounted (hidden when collapsed) so `aria-controls` is never
                a dangling IDREF — see the direct-shared list above (#6453). */}
            <ul
              id={groupSharedListId}
              hidden={!isGroupSharedOpen}
              className='space-y-0.5 pt-1 pb-1'
            >
              {groupSharedSessions.map((session) => (
                <SessionItem
                  key={session.session_id}
                  session={session}
                  showContextBadge={showContextBadge}
                  onSessionClick={onSessionClick}
                />
              ))}
            </ul>
          </div>
        )}
        {/* Own sessions — shown below shared folders */}
        {sortedSessions.length > 0 && (
          <SessionList
            groupedSessions={groupedSessions}
            groupOrder={groupOrder}
            showContextBadge={showContextBadge}
            onSessionClick={onSessionClick}
            showGroupHeaders={recentsGroupBy === 'date'}
          />
        )}
        {/* Load-more footer: spinner while loading, error+retry on failure */}
        {pagination.isLoadingMore && (
          <div className='flex justify-center py-2'>
            <Loader2 className='h-4 w-4 animate-spin text-content-muted' />
          </div>
        )}
        {pagination.loadMoreError && !pagination.isLoadingMore && (
          <div className='flex justify-center py-2'>
            <button
              type='button'
              onClick={loadMoreSessions}
              className='flex items-center gap-1 text-xs text-content-secondary hover:text-content-primary transition-colors cursor-pointer min-h-[44px]'
            >
              <RefreshCw className='h-3 w-3' />
              {t('retryLoadMore')}
            </button>
          </div>
        )}
      </>
    ) : (
      <div className='flex justify-center items-center h-full text-center text-content-muted'>
        <div className='flex flex-col items-center'>
          <span className='text-sm'>
            {tLoader('noHistory', { ns: 'loading' })}
          </span>
        </div>
      </div>
    );

  // Session list for non-chat list sections (search / document_analysis). Same
  // date-grouped list as chat, minus the chat-only chrome (new-chat, search-
  // chats, shared folders). Owns its own auth / loading / empty states.
  const sectionSessionList =
    isAuthLoading || isLoading ? (
      <AppSidebarSkeleton />
    ) : !isAuthenticated ? (
      <div className='flex justify-center items-center h-full text-center text-content-muted'>
        <div className='flex flex-col items-center'>
          <LoginButton t={tHeader as (key: string) => string} />
        </div>
      </div>
    ) : sortedSessions.length > 0 ? (
      <>
        <SessionList
          groupedSessions={groupedSessions}
          groupOrder={groupOrder}
          showContextBadge={showContextBadge}
          onSessionClick={onSessionClick}
          sessionPathname={sectionSessionPathname}
          showGroupHeaders={recentsGroupBy === 'date'}
        />
        {pagination.isLoadingMore && (
          <div className='flex justify-center py-2'>
            <Loader2 className='h-4 w-4 animate-spin text-content-muted' />
          </div>
        )}
        {pagination.loadMoreError && !pagination.isLoadingMore && (
          <div className='flex justify-center py-2'>
            <button
              type='button'
              onClick={loadMoreSessions}
              className='flex items-center gap-1 text-xs text-content-secondary hover:text-content-primary transition-colors cursor-pointer min-h-[44px]'
            >
              <RefreshCw className='h-3 w-3' />
              {t('retryLoadMore')}
            </button>
          </div>
        )}
      </>
    ) : (
      <div className='flex justify-center items-center h-full text-center text-content-muted'>
        <div className='flex flex-col items-center'>
          <span className='text-sm'>
            {tLoader('noHistory', { ns: 'loading' })}
          </span>
        </div>
      </div>
    );

  // The recents list shown below the nav. Chat routes (and any unmapped route)
  // show the chat history; search / document-analysis routes show that
  // section's sessions. Owns its own scroll so a long virtualized list stays
  // bounded and the infinite-scroll handler attaches to the real scroll element.
  const recents = isAnonymousMode ? undefined : (
    <div
      ref={scrollContainerRef}
      className='min-h-0 flex-1 overflow-y-auto overscroll-contain touch-pan-y pb-2'
      style={{ WebkitOverflowScrolling: 'touch' }}
      onScroll={handleSessionListScroll}
    >
      {sessionListType === 'search' ||
      sessionListType === 'document_analysis' ||
      sessionListType === 'document_generation'
        ? sectionSessionList
        : chatHistory}
    </div>
  );

  // Render the single-column sidebar (logo + nav + recents + footer).
  return (
    <div className='relative h-full w-full overflow-hidden bg-surface-sidebar'>
      {/* Search dialog */}
      <SearchHistoryDialog
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />

      <SidebarRail
        isAnonymousMode={isAnonymousMode}
        isPremiumActive={isPremiumActive}
        // Desktop: the header collapse button shrinks to the 56px rail. Mobile:
        // the drawer is dismissed by its backdrop / nav taps, so no button.
        onCollapse={isMobileView ? undefined : onRequestCollapse}
        // Mobile: close the drawer once navigation starts.
        onNavigate={isMobileView ? () => onRequestCollapse?.() : undefined}
        // Opens the ⌘K search palette (the dialog mounted in this component).
        // Anonymous users have no chat history, so the Search action is hidden
        // for them (an empty palette would be a dead end).
        onSearchChats={
          isAnonymousMode ? undefined : () => setIsSearchOpen(true)
        }
        recents={recents}
        // Dragging the menu/Recents split is a pointer affordance: on the
        // mobile drawer there is no cursor and the strip would sit under the
        // 44px touch floor, so the boundary stays a plain divider there.
        canResizeSplit={!isMobileView}
        // Recents "Group by" control (None / Date) — only meaningful when there
        // is a recents list, so it's gated on a non-anon recents below.
        recentsGroupBy={recentsGroupBy}
        onRecentsGroupByChange={isAnonymousMode ? undefined : setRecentsGroupBy}
        usageSlot={
          isAuthenticated ? (
            <SidebarUsageInfo
              isUsageLoading={isUsageLoading}
              usageData={usageData}
              usageError={usageError}
              t={t as (key: string) => string}
              onRetry={fetchUsageData}
            />
          ) : undefined
        }
        anonFooterSlot={<SidebarAnonFooter />}
      />

      {/* Right-edge resize strip — drag left to collapse the whole sidebar
          (desktop only). An 8px target with a grabber that fades in on hover. */}
      {!isMobileView && (
        <div
          className='group/resize absolute bottom-0 right-0 top-0 z-[100] w-2 cursor-ew-resize bg-transparent transition-colors hover:bg-brand-tertiary/20'
          aria-hidden='true'
          onMouseDown={handleDragStart}
        >
          <span className='absolute right-0 top-1/2 h-8 w-1 -translate-y-1/2 rounded-full bg-line-strong opacity-0 transition-opacity group-hover/resize:opacity-100' />
        </div>
      )}
    </div>
  );
};
